import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertTriangle, Copy, Loader2 } from 'lucide-react';
import { useCreate, useNotify, useRecordContext, useUpdate } from 'ra-core';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useHasPermission } from '../rbac/useHasPermission';
import type { BoM } from '../types';

export const SupersedeBoMButton = () => {
    const record = useRecordContext<BoM>();
    const notify = useNotify();
    const [create] = useCreate();
    const [update] = useUpdate();
    const navigate = useNavigate();

    const [open, setOpen] = useState(false);
    const [newVersion, setNewVersion] = useState('');
    const [loading, setLoading] = useState(false);
    const { hasPermission: canManage } = useHasPermission('manage_production');

    if (!record || record.status !== 'approved' || !canManage) return null;

    // Suggest next version
    const currentVer = parseFloat(record.version) || 1.0;
    const suggestedVer = (currentVer + 0.1).toFixed(1);

    const handleSupersede = async () => {
        setLoading(true);
        try {
            // 1. Create new BoM (copy of current)
            create('boms', {
                data: {
                    ...record,
                    id: undefined, // Let DB generate new ID
                    version: newVersion || suggestedVer,
                    status: 'draft',
                    reference: `${record.reference}-V${newVersion || suggestedVer}`,
                    created_at: new Date().toISOString(),
                    // New draft hasn't been through approval yet — don't carry
                    // over the previous version's sign-off, or the readiness
                    // checklist it was signed off against.
                    approved_by: null,
                    approved_at: null,
                    readiness_checklist: [],
                }
            }, {
                onSuccess: (newRecord) => {
                    // 2. Mark current as superseded
                    update('boms', {
                        id: record.id,
                        data: { status: 'superseded' },
                        previousData: record
                    }, {
                        onSuccess: () => {
                            notify('New version created. Previous version superseded.', { type: 'success' });
                            setOpen(false);
                            navigate(`/boms/${newRecord.id}/show`);
                        }
                    });
                },
                onError: (error: any) => {
                    notify(`Error: ${error.message}`, { type: 'error' });
                    setLoading(false);
                }
            });
        } catch (error: any) {
            notify(`Error: ${error.message}`, { type: 'error' });
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant="outline" className="gap-2 h-9 border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100">
                    <Copy className="w-4 h-4" />
                    Supersede (New Version)
                </Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className="w-5 h-5 text-amber-500" />
                        <DialogTitle>Supersede BoM</DialogTitle>
                    </div>
                    <DialogDescription>
                        This will mark the current BoM as **Superseded** and create a new **Draft** copy for further edits.
                    </DialogDescription>
                </DialogHeader>
                <div className="py-4 space-y-4">
                    <div className="space-y-2">
                        <Label>Current Version</Label>
                        <Input value={record.version} disabled className="bg-muted" />
                    </div>
                    <div className="space-y-2">
                        <Label>New Version Label</Label>
                        <Input 
                            placeholder={`e.g. ${suggestedVer}`} 
                            value={newVersion} 
                            onChange={(e) => setNewVersion(e.target.value)}
                        />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                    <Button 
                        onClick={handleSupersede} 
                        disabled={loading}
                        className="bg-amber-600 hover:bg-amber-700 text-white"
                    >
                        {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Create New Version
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
